import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  Switch,
  useToast,
} from '@ais-ui/ui-core';
import * as z from 'zod';
import { useSettings } from '../../SettingsProvider';
import { useSaveSettings } from '@ais-ui/websockets';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@ais-ui/utils';
import React, { useEffect, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettingsPage } from '../../SettingsPageProvider';

const labelFormSchema = z.object({
  country: z.boolean(),
  latitude: z.boolean(),
  longitude: z.boolean(),
  mmsi: z.boolean(),
  vdlLoad: z.boolean(),
});

type LabelFormValues = z.infer<typeof labelFormSchema>;

export const BaseStationHoverLabelForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = {
    country:
      settings['ribbonPlugin.settings']?.show_basestation_country || false,
    latitude:
      settings['ribbonPlugin.settings']?.show_basestation_latitude || false,
    longitude:
      settings['ribbonPlugin.settings']?.show_basestation_longitude || false,
    mmsi: settings['ribbonPlugin.settings']?.show_basestation_mmsi || false,
    vdlLoad:
      settings['ribbonPlugin.settings']?.show_basestation_vdl_load || false,
  };

  const form = useForm<LabelFormValues>({
    resolver: zodResolver(labelFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: LabelFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        show_basestation_country: data.country,
        show_basestation_latitude: data.latitude,
        show_basestation_longitude: data.longitude,
        show_basestation_mmsi: data.mmsi,
        show_basestation_vdl_load: data.vdlLoad,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-basestation-hover-settings-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsMapHoverSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-basestation-hover-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsMapHoverFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                key="mmsi"
                control={form.control}
                name="mmsi"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapBaseStationHoverMmsiLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapBaseStationHoverMmsiDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-baseStationLabel-mmsi"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="country"
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapBaseStationHoverCountryLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapBaseStationHoverCountryDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-baseStationLabel-country"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="latitude"
                control={form.control}
                name="latitude"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapBaseStationHoverLatitudeLabel}
                      </FormLabel>
                      <FormDescription>
                        {
                          messages.settingsMapBaseStationHoverLatitudeDescription
                        }
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-baseStationLabel-latitude"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="longitude"
                control={form.control}
                name="longitude"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapBaseStationHoverLongitudeLabel}
                      </FormLabel>
                      <FormDescription>
                        {
                          messages.settingsMapBaseStationHoverLongitudeDescription
                        }
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-baseStationLabel-longitude"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="vdlLoad"
                control={form.control}
                name="vdlLoad"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapBaseStationHoverVdlLoadLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapBaseStationHoverVdlLoadDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-baseStationLabel-country"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-baseStationLabel-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
